import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { IonButton, IonIcon, ModalController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { arrowForward, sparklesOutline } from 'ionicons/icons';

import { ComponentAccess } from '../../../../common/decorators/component-access.decorator';
import {
  ONBOARDING_SLIDES,
  OnboardingCta,
} from './onboarding-slides.const';

/** What the modal tells its caller to do next. */
export type WelcomeResult = 'tour' | 'dismissed';

/**
 * First-run welcome carousel: four slides explaining Programs / Library /
 * Discover before the user has any content of their own, ending in a choice
 * between the guided shell tour and exploring alone.
 *
 * Presented by `TabsPage` on first launch and re-presentable from the menu's
 * "Getting started" entry. It owns no persistence — the caller marks the flag
 * off the result, so a replay from the menu doesn't have to special-case it.
 */
@Component({
  selector: 'app-welcome-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonIcon],
  templateUrl: './welcome-modal.component.html',
  styleUrls: ['./welcome-modal.component.scss'],
})
@ComponentAccess('WelcomeModalComponent')
export class WelcomeModalComponent {
  private readonly modalCtrl = inject(ModalController);
  private readonly track = viewChild.required<ElementRef<HTMLElement>>('track');

  protected readonly slides = ONBOARDING_SLIDES;
  protected readonly Cta = OnboardingCta;

  protected readonly index = signal(0);
  protected readonly isLast = computed(() => this.index() === this.slides.length - 1);

  constructor() {
    addIcons({ arrowForward, sparklesOutline });
  }

  /** Track the snapped slide so the dots and the footer CTA follow a swipe. */
  protected onScroll(): void {
    const el = this.track().nativeElement;
    if (!el.clientWidth) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== this.index()) this.index.set(i);
  }

  protected next(): void {
    this.goTo(this.index() + 1);
  }

  protected goTo(i: number): void {
    const el = this.track().nativeElement;
    const clamped = Math.max(0, Math.min(i, this.slides.length - 1));
    el.scrollTo({
      left: clamped * el.clientWidth,
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    });
    // Set eagerly rather than waiting for the scroll to settle, so the dots and
    // the Next/CTA swap track the tap without a visible lag.
    this.index.set(clamped);
  }

  protected startTour(): void {
    void this.modalCtrl.dismiss('tour' satisfies WelcomeResult);
  }

  protected dismiss(): void {
    void this.modalCtrl.dismiss('dismissed' satisfies WelcomeResult);
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }
}
